import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PageShell } from "@/components/site/PageShell";
import { robotsMeta } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { crmPublicObtenerPerfilPostulante } from "@/server/crm/postulante-perfil.fns";
import { VerificarIdentidadForm } from "@/components/postulante/VerificarIdentidadForm";
import { MisDatosSection } from "@/components/postulante/MisDatosSection";
import { MisFotosSection } from "@/components/postulante/MisFotosSection";
import { FichaPortafolioPreview } from "@/components/postulante/FichaPortafolioPreview";
import type { FichaPostulanteDTO, PostulantePerfilDTO } from "@/lib/crm/types";

// Sesión por pestaña, nunca en la URL (FR-007): se restaura al recargar la
// misma pestaña, no persiste entre navegadores ni se puede compartir.
const SESSION_KEY = "postulante.sesion";

export const Route = createFileRoute("/mi-perfil")({
  head: () => ({
    meta: [{ title: "Mi perfil — Improve Yourself" }, ...robotsMeta(false, false)],
  }),
  component: MiPerfilPage,
});

type Tab = "datos" | "fotos" | "ficha";
const TABS: Array<{ id: Tab; label: string }> = [
  { id: "datos", label: "Mis datos" },
  { id: "fotos", label: "Mis fotos" },
  { id: "ficha", label: "Mi ficha" },
];

function SesionVigenciaAviso({ expiraEn, onExpirar }: { expiraEn: string; onExpirar: () => void }) {
  const [restanteMs, setRestanteMs] = useState(() => new Date(expiraEn).getTime() - Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      const ms = new Date(expiraEn).getTime() - Date.now();
      setRestanteMs(ms);
      if (ms <= 0) onExpirar();
    }, 15_000);
    return () => clearInterval(id);
  }, [expiraEn, onExpirar]);

  if (restanteMs > 5 * 60 * 1000) return null;
  const minutos = Math.max(0, Math.ceil(restanteMs / 60_000));
  return (
    <p
      role="status"
      className="mt-4 rounded-lg bg-amber-50 px-4 py-2.5 text-xs font-medium text-amber-800"
    >
      Tu sesión vence en {minutos} min. Guarda tus cambios antes de que expire.
    </p>
  );
}

function MiPerfilPage() {
  const [restaurando, setRestaurando] = useState(false);
  const [sesionToken, setSesionToken] = useState<string | null>(null);
  const [perfil, setPerfil] = useState<PostulantePerfilDTO | null>(null);
  const [ficha, setFicha] = useState<FichaPostulanteDTO | null>(null);
  const [tab, setTab] = useState<Tab>("datos");
  const [sesionExpirada, setSesionExpirada] = useState(false);

  useEffect(() => {
    let guardado: string | null = null;
    try {
      guardado = sessionStorage.getItem(SESSION_KEY);
    } catch {
      // sessionStorage puede fallar en navegación privada
    }
    // Sin token guardado (el caso normal: SSR nunca tiene acceso a
    // sessionStorage, así que el primer render siempre asume esto) no hay
    // nada que restaurar — el formulario de verificación ya está visible.
    if (!guardado) return;

    setRestaurando(true);
    crmPublicObtenerPerfilPostulante({ data: { sesion_token: guardado } })
      .then((r) => {
        setSesionToken(guardado);
        setPerfil(r.perfil);
        setFicha(r.ficha);
      })
      .catch(() => {
        try {
          sessionStorage.removeItem(SESSION_KEY);
        } catch {
          // no-op
        }
      })
      .finally(() => setRestaurando(false));
  }, []);

  function onVerificado(token: string, perfilNuevo: PostulantePerfilDTO) {
    try {
      sessionStorage.setItem(SESSION_KEY, token);
    } catch {
      // sin sessionStorage, la sesión igual funciona hasta que se recargue la página
    }
    setSesionToken(token);
    setPerfil(perfilNuevo);
    setSesionExpirada(false);
    setTab("datos");
    crmPublicObtenerPerfilPostulante({ data: { sesion_token: token } })
      .then((r) => setFicha(r.ficha))
      .catch(() => {});
  }

  function limpiarSesionLocal() {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // no-op
    }
    setSesionToken(null);
    setPerfil(null);
    setFicha(null);
  }

  function onSesionInvalida() {
    limpiarSesionLocal();
    setSesionExpirada(true);
  }

  function onActualizado(perfilNuevo: PostulantePerfilDTO, fichaNueva: FichaPostulanteDTO) {
    setPerfil(perfilNuevo);
    setFicha(fichaNueva);
  }

  if (!sesionToken || !perfil) {
    // El formulario se muestra de inmediato (incluido el HTML del SSR): la
    // gran mayoría de las visitas no tiene una sesión guardada en
    // sessionStorage (inexistente durante el SSR), así que bloquear el
    // primer render hasta resolver esa comprobación dejaría la página en
    // blanco para el caso común. Solo se oculta brevemente mientras se
    // restaura una sesión de la misma pestaña (caso raro: volver a
    // /mi-perfil sin haber cerrado sesión, dentro de los 45 min de vigencia).
    return (
      <PageShell>
        {/* pt-28/md:pt-36 (no py-*): el SiteNav es `fixed top-0`, así que no
            reserva espacio en el flujo normal — sin este margen, su altura en
            móvil corta el título. Mismo valor que usa PageHero para el resto
            del sitio. */}
        <div className="mx-auto max-w-lg px-5 pb-12 pt-28 md:pb-16 md:pt-36">
          <h1 className="font-display text-2xl font-bold text-secondary">Mi perfil</h1>
          <p className="mt-2 text-sm text-secondary/70">
            Verifica tu identidad para actualizar tus datos, tus fotos, o ver cómo te vería un
            cliente.
          </p>
          {sesionExpirada && (
            <p
              role="status"
              className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              Tu sesión expiró. Verifica tu identidad de nuevo.
            </p>
          )}
          {restaurando ? (
            <p role="status" className="mt-6 text-sm text-secondary/60">
              Restaurando tu sesión…
            </p>
          ) : (
            <div className="mt-6">
              <VerificarIdentidadForm onVerificado={onVerificado} />
            </div>
          )}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* pt-28/md:pt-36: mismo margen que reserva PageHero para el SiteNav
          fijo — sin esto, "Hola, {nombre}" queda cortado en móvil. */}
      <div className="mx-auto max-w-2xl px-5 pb-10 pt-28 md:pb-14 md:pt-36">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-secondary">
              Hola, {perfil.nombre.split(/\s+/)[0]}
            </h1>
            <p className="mt-1 text-sm text-secondary/70">
              {perfil.revision === "pendiente_de_revision"
                ? "Tu postulación está pendiente de revisión."
                : `Promotor activo · #${perfil.codigo}`}
            </p>
          </div>
          <button
            type="button"
            onClick={limpiarSesionLocal}
            className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-secondary/70 transition hover:bg-surface"
          >
            Cerrar sesión
          </button>
        </header>

        <SesionVigenciaAviso expiraEn={perfil.sesionExpiraEn} onExpirar={onSesionInvalida} />

        <div className="mt-6 flex gap-1 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                "border-b-2 px-3 py-2 text-sm font-semibold transition",
                tab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-secondary/60 hover:text-secondary",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "datos" && (
            <MisDatosSection
              sesionToken={sesionToken}
              perfil={perfil}
              onActualizado={onActualizado}
              onSesionInvalida={onSesionInvalida}
            />
          )}
          {tab === "fotos" && ficha && (
            <MisFotosSection
              sesionToken={sesionToken}
              perfil={perfil}
              ficha={ficha}
              onActualizado={onActualizado}
              onSesionInvalida={onSesionInvalida}
            />
          )}
          {tab === "ficha" && ficha && (
            <FichaPortafolioPreview ficha={ficha} onIrAFotos={() => setTab("fotos")} />
          )}
        </div>
      </div>
    </PageShell>
  );
}
